/**
 * EdgeTab — 화면 가장자리에 붙는 접힌 탭. 고정 버튼(FAB)을 화면 방해 없이 상주시키는 방식.
 *
 * 왜 이 모양인가: 회원 화면에서 **고정으로 비어 있는 자리는 위아래에 없다.** 위는 헤더
 * (.header_in 55px)의 뒤로/햄버거 버튼, 아래는 전폭 CTA(.sj-foot · BS 하단 버튼 · 발주 제출영역)다.
 * 남는 곳은 가장자리 세로중앙뿐이라 거기 붙이고, 평소엔 아이콘만 남겨 면적을 줄인다.
 *
 * 그래도 페이지마다 걸리는 것이 달라 고정 좌표 하나로는 못 맞춘다. 그래서 **끌어서 옮긴다** —
 * 상하는 끈 자리 그대로, 좌우는 놓은 쪽 가장자리에 붙는다. 그 자리를 기기에 기억한다.
 *
 * 사용:
 *   $j.Ui.EdgeTab.attach('#ai-bal-fab', { key: 'aiBal', onTap: () => openChat() });
 *   const ctl = $j.Ui.EdgeTab.attach(el, {...});   // { peek, reset, side, destroy }
 *
 * 마크업 규약 — 아이콘과 라벨을 나눠 둔다(라벨만 접힌다):
 *   <button class="jb-edge-tab jb-edge-tab--ai"><i>🤖</i><em>AI로 발주</em></button>
 *
 * opts
 *   key    localStorage 키 접미사 → 'm:ui:edgeTab:{key}'. 없으면 옮긴 자리를 기억하지 않는다.
 *   side   기본 가장자리 'r'|'l' (기본 'r'). 저장된 자리가 있으면 그쪽이 이긴다.
 *   slot   **끌어 옮기기 전** 기본 세로자리 — 0(세로중앙) | 1(그 아래) | 'bottom'(화면 하단).
 *          scss 가 좌표를 준다. 옮기고 나면 저장된 자리가 이긴다.
 *   peek     진입 직후 라벨을 펼쳐 두는 시간(ms, 기본 2000). 0 이면 처음부터 접힘.
 *   collapse 기본 true(아이콘만 남기고 접음). false 면 라벨을 **계속** 펼쳐 둔다 —
 *            라벨 자체가 숨으면 안 되는 정보일 때만 쓴다(예: 지금 누구로 대리진입 중인지).
 *   dismiss  true 면 붙어 있는 쪽 가장자리 끝까지 밀어 넣어 **지금 보고 있는 화면에서만** 치운다
 *            (화면 캡쳐할 때 걸리적거리지 않게). 치운 사실은 저장하지 않는다 — 새로고침하거나
 *            다른 화면으로 가면 옮겨 두었던 자리에 다시 뜬다. 유일한 출구(복귀 알약)에는 켜지 않는다.
 *   onTap  짧게 눌렀을 때. 호출부가 자기 click 리스너를 따로 달아도 된다(아래 참조).
 *
 * 동작 메모
 * - 세로 위치는 **화면높이 대비 비율**로 저장한다. 기기·회전·주소창 유무로 높이가 달라지는데
 *   px 로 저장하면 작은 화면에서 화면 밖에 놓인다. 읽을 때 clamp 도 한 번 더 건다.
 * - **자리 계산은 전부 화면좌표(px)로 하고, style.top 에 쓸 때만 zoom 으로 나눈다.**
 *   글자크기 3단계는 `body` 에 zoom 을 건다(mobile/base/_fontsize.scss — l:1.2, xl:1.4).
 *   탭은 body 자식이라 `top:700px` 이 화면에서는 700×1.4=980px 에 그려진다. 그런데
 *   getBoundingClientRect() 는 화면좌표(980)를 돌려주므로, 이 둘을 섞으면 끄는 순간 탭이
 *   배율만큼 아래로 튀고(잘못 이동), 화면 밖 좌표가 그대로 저장돼 다음에 열어도 안 보였다.
 *   ('아주크게'에서 세로중앙보다 아래로 옮기면 재현된다. top:50% 는 뷰포트 기준이라
 *    옮기기 전 기본자리는 멀쩡했고, 그래서 '옮기면 사라진다'로 보였다)
 * - 놓을 수 있는 구간은 **지금 눈에 보이는 세로 구간**이다(visibleBand). window.innerHeight 가
 *   아니라 visualViewport 를 본다 — 안드로이드 웹뷰는 키보드가 뜰 때(adjustPan) innerHeight 는
 *   그대로 둔 채 보이는 영역만 줄이고 위로 민다. innerHeight 로 자르면 그 상태에서 옮긴 탭이
 *   키보드 뒤(=화면 밖)에 앉아 그대로 저장되고, 다음에 열어도 안 보인다.
 * - 저장하는 비율도 **화면좌표 기준**이다. 그래서 글자크기를 바꿔도 탭은 눈에 보이던 같은
 *   높이에 그대로 있는다.
 * - 그래서 붙일 때 한 번 **눈에 들어오는지 검사한다**(applyPos({validate:true})). 화면 안에
 *   통째로 들어오지 못하는 자리면 저장을 버리고 기본자리로 되돌린다. 머리/발치 여백(PAD)만
 *   침범한 정도면 버리지 않고 끌어당기기만 한다 — 회전 한 번에 옮겨둔 자리를 잃으면 안 된다.
 * - 드래그 뒤 이어지는 click 은 **캡쳐단계에서** 삼킨다. 호출부가 나중에 단 bubble 리스너보다
 *   먼저 돌기 때문에, EdgeTab 을 먼저 attach 해두면 옮기자마자 눌린 것으로 처리되지 않는다.
 * - 세로 정렬에 transform 을 쓰지 않는다(top 으로만 잡는다). transform 을 쓰는 다른 모듈
 *   (FabHide 등)과 한 요소에서 겹치면 서로 덮어쓴다.
 * - 저장 형식은 `{side, y}` JSON 이다. 예전엔 'l'/'r' 글자만 넣었으므로 그 값도 읽어 준다.
 */
const LS_PREFIX = 'm:ui:edgeTab:';

const SIDE_L = 'l';
const SIDE_R = 'r';

const PEEK_MS  = 2000;
const DRAG_MIN = 6;    // 이만큼 움직여야 '옮기기'로 본다(px) — 탭과 구분
const PAD_TOP    = 60; // 헤더(55px) 아래로만 놓이게
const PAD_BOTTOM = 76; // 하단 전폭 CTA 위로만 놓이게
const DISMISS_EDGE = 10;  // 손가락이 가장자리에서 이 안(px)까지 닿으면 '끝까지 밀었다'로 본다
const LEAVE_MS     = 180; // 밀려 나가는 전환 시간 — scss 의 left/right transition 과 맞춘다

export default class EdgeTab {
    attach(target, opts = {}) {
        const el = typeof target === 'string' ? document.querySelector(target) : target;
        if (!el || el.dataset.edgeTab) return null;
        el.dataset.edgeTab = '1';
        el.classList.add('jb-edge-tab');

        const storeKey = opts.key ? LS_PREFIX + opts.key : '';
        const canFold  = opts.collapse !== false;
        const canDismiss = opts.dismiss === true;
        const peekMs   = canFold ? (opts.peek ?? PEEK_MS) : 0;
        const defSide  = opts.side === SIDE_L ? SIDE_L : SIDE_R;
        el.dataset.slot = String(opts.slot ?? 0);   // 0 | 1 | 'bottom' (scss 가 좌표를 준다)

        let peekTimer = null;
        let moved = false;              // 방금 '옮기기'가 있었나 — 뒤따르는 click 을 삼키는 표식
        let dragging = false;
        let pointerId = null;           // 누르고 있는 포인터. null 이면 '안 눌림' — 마우스 hover 를 거른다
        let startX = 0, startY = 0, startTop = 0;
        let startSide = SIDE_R;         // 누를 때 붙어 있던 쪽 — 밀어 치우기는 이쪽 가장자리로만 된다
        let leaveTimer = null;

        /* ── 저장/복원 ─────────────────────────────────────────────────── */

        /** @returns {{side?:string, y?:number}} */
        const read = () => {
            if (!storeKey) return {};
            const raw = $j.Common.Store.local.get(storeKey);   // Store 가 try/catch 내장
            if (raw === SIDE_L || raw === SIDE_R) return { side: raw };   // 구 형식(가장자리 글자만)
            try { return JSON.parse(raw) || {}; } catch (e) { return {}; }
        };
        const save = (side, y) => {
            if (storeKey) $j.Common.Store.local.setJSON(storeKey, { side, y });
        };
        const clearPos = () => {
            if (storeKey) $j.Common.Store.local.remove(storeKey);
        };

        const side = () => {
            const s = read().side;
            return (s === SIDE_L || s === SIDE_R) ? s : defSide;
        };

        /**
         * 지금 걸려 있는 확대배율. 글자크기 3단계가 body 에 zoom 을 걸기 때문에
         * style.top(로컬 px)과 화면좌표가 이 배율만큼 어긋난다.
         * 실측(화면높이÷레이아웃높이)이 정확하고, 탭이 숨어 있어 잴 수 없을 때만 body 값을 읽는다.
         */
        const zoom = () => {
            const oh = el.offsetHeight;
            const rh = el.getBoundingClientRect().height;
            let z = (oh && rh) ? rh / oh : parseFloat(getComputedStyle(document.body).zoom);
            if (!isFinite(z) || z <= 0) z = 1;
            return z;
        };

        /**
         * 지금 눈에 보이는 세로 구간(화면좌표).
         * visualViewport 가 없으면(구 웹뷰) innerHeight 를 그대로 쓴다.
         */
        const visibleBand = () => {
            const vv = window.visualViewport;
            const top = vv ? vv.offsetTop : 0;
            const height = (vv ? vv.height : window.innerHeight) || window.innerHeight || 0;
            return { top, height, bottom: top + height };
        };

        /**
         * 놓을 수 있는 세로 구간 [min, max] — 화면좌표. 보이는 구간에서 머리(헤더)·발치(CTA)를 뺀다.
         * 여백도 배율을 탄다 — 가려야 할 헤더·CTA 가 body 안에 있어 같이 확대되기 때문이다.
         */
        const range = () => {
            const z = zoom();
            const h = (el.offsetHeight || 44) * z;      // 화면에서의 탭 높이
            const v = visibleBand();
            const min = v.top + PAD_TOP * z;
            return { min, max: Math.max(min, v.bottom - h - PAD_BOTTOM * z), h, z };
        };

        /** 화면 안에 있도록 세로좌표(화면좌표)를 자른다 */
        const clampTop = (top) => {
            const { min, max } = range();
            if (!isFinite(top)) return min;
            return Math.min(Math.max(top, min), max);
        };

        /** 이 화면좌표에 놓으면 탭이 보이는 구간 안에 통째로 들어오는가 */
        const fitsInView = (top) => {
            const { h } = range();
            const v = visibleBand();
            if (!isFinite(top) || !v.height) return false;
            return top >= v.top && top + h <= v.bottom;
        };

        /** 화면좌표를 style.top(로컬 px)으로 환산해 박는다 */
        const setTop = (screenTop) => { el.style.top = (screenTop / zoom()) + 'px'; };

        /** 지금 탭의 화면좌표 */
        const curTop = () => el.getBoundingClientRect().top;

        /**
         * 저장된 자리를 화면에 반영. 옮긴 적이 없으면 인라인 top 을 비워 scss 기본(슬롯)을 쓴다.
         *
         * @param {{validate?:boolean}} o validate 면 '보이는 구간 밖'인 저장값을 버리고 기본자리로
         *        되돌린다. 붙일 때만 켠다 — 키보드가 떠서 잠깐 구간이 좁아진 것뿐인데 리사이즈마다
         *        지워버리면 옮겨둔 자리가 남아나지 않는다.
         */
        const applyPos = (o = {}) => {
            const st = read();
            el.dataset.side = side();

            const y = st.y;
            const bad = typeof y !== 'number' || !isFinite(y) || y <= 0 || y >= 1;
            if (bad) {
                // 형식이 깨진 값(옛 버전·수기수정)은 남겨둘 이유가 없다
                if (typeof y !== 'undefined') clearPos();
                el.style.top = '';
                el.style.bottom = '';
                return;
            }

            // 저장값은 화면좌표 기준 비율이다 — 글자크기를 바꿔도 보이던 높이에 그대로 있게.
            const { h } = range();
            const raw = y * window.innerHeight - h / 2;

            // 눈에 안 들어오는 자리면 저장을 버리고 기본자리로. (PAD 만 침범한 정도는 clamp 로 끝낸다)
            if (o.validate && !fitsInView(raw)) {
                clearPos();
                el.style.top = '';
                el.style.bottom = '';
                el.dataset.side = defSide;
                return;
            }

            // bottom 기준 슬롯('bottom')을 쓰던 탭도 옮긴 뒤에는 top 하나로만 잡는다.
            // 둘 다 남겨두면 height 가 고정이라 top 이 이기긴 하지만, 의도를 코드로 남긴다.
            el.style.bottom = 'auto';
            setTop(clampTop(raw));
        };

        /* ── 표시 ──────────────────────────────────────────────────────── */

        /** 라벨을 잠깐 펼쳤다 접는다 — '여기 있다'를 알리는 용도. 접지 않는 탭이면 계속 펼쳐 둔다. */
        const peek = () => {
            if (!canFold) { el.classList.add('is-open'); return; }
            if (!peekMs) return;
            clearTimeout(peekTimer);
            el.classList.add('is-open');
            peekTimer = setTimeout(() => el.classList.remove('is-open'), peekMs);
        };

        /* ── 밀어 치우기 (opts.dismiss) ─────────────────────────────────── */

        /**
         * 손가락이 **누를 때 붙어 있던 쪽** 가장자리 끝에 닿았나.
         * 반대편 끝은 보지 않는다 — 오른쪽 탭을 왼쪽 끝까지 끄는 것은 '왼쪽으로 옮기기'다.
         */
        const atEdge = (x) => el.dataset.side === startSide
            && (startSide === SIDE_L ? x <= DISMISS_EDGE : x >= window.innerWidth - DISMISS_EDGE);

        /**
         * 붙은 쪽으로 민 만큼 탭을 가장자리 밖으로 끌어낸다(화면좌표 px). 0 이면 제자리.
         * 끝에 닿으면 흐려져 '놓으면 사라진다'를 놓기 전에 알린다.
         * transform 대신 left/right 를 쓴다 — 머리말 '세로 정렬에 transform 을 쓰지 않는다'와 같은 이유.
         */
        const setPush = (px, x) => {
            el.style.left = '';
            el.style.right = '';
            if (px > 0) el.style[el.dataset.side === SIDE_L ? 'left' : 'right'] = (-px / zoom()) + 'px';
            el.classList.toggle('is-dismissing', px > 0 && atEdge(x));
        };

        /** 가장자리 밖으로 밀려 나간 뒤 숨는다. 자리는 저장하지 않는다. */
        const dismiss = () => {
            clearTimeout(peekTimer);
            el.classList.remove('is-open', 'is-dismissing');
            el.style[el.dataset.side === SIDE_L ? 'left' : 'right'] = (-(el.offsetWidth + 16)) + 'px';
            leaveTimer = setTimeout(() => el.classList.add('is-dismissed'), LEAVE_MS);
        };

        /** 치운 탭을 되살린다 (bfcache 복귀·reset) */
        const undismiss = () => {
            clearTimeout(leaveTimer);
            el.classList.remove('is-dismissed', 'is-dismissing');
            el.style.left = '';
            el.style.right = '';
        };

        /** 옮긴 자리를 잊고 기본 자리로 되돌린다 */
        const reset = () => {
            undismiss();
            clearPos();
            el.style.top = '';
            el.style.bottom = '';
            el.dataset.side = defSide;
            peek();
        };

        /* ── 끌어 옮기기 ───────────────────────────────────────────────── */

        const onDown = (e) => {
            if (e.button > 0) return;         // 마우스 우클릭·가운데버튼은 옮기기가 아니다
            moved     = false;                // 옮긴 뒤 click 이 안 오는 웹뷰 대비 — 다음 누름에서 초기화
            dragging  = false;
            pointerId = e.pointerId;
            startX    = e.clientX;
            startY    = e.clientY;
            startTop  = curTop();             // 화면좌표. clientY 와 같은 축이라 dy 를 그대로 더한다
            startSide = el.dataset.side === SIDE_L ? SIDE_L : SIDE_R;
            el.setPointerCapture?.(e.pointerId);
        };

        const onMove = (e) => {
            // ⚠️ 누르고 있을 때만 움직인다. 이 가드가 없으면 **마우스를 올리기만 해도** 따라온다 —
            //    pointermove 는 hover 로도 오는데, 그때 startX/startY 는 직전 누름(없으면 0)의 값이라
            //    거리 판정이 곧바로 통과해 드래그가 시작된다. 터치엔 hover 가 없어 PC 에서만 보였다.
            if (pointerId === null || e.pointerId !== pointerId) return;

            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            if (!dragging) {
                if (Math.abs(dx) < DRAG_MIN && Math.abs(dy) < DRAG_MIN) return;
                dragging = true;
                moved    = true;
                el.classList.add('is-dragging');   // 전환효과를 끈다(끌리는 동안 따라붙게)
                el.style.bottom = 'auto';          // 하단기준 슬롯이었으면 top 기준으로 넘긴다
            }
            setTop(clampTop(startTop + dy));
            // 좌우는 끄는 도중에도 미리 붙여 보여준다 — 놓기 전에 어느 쪽에 앉을지 알 수 있게
            el.dataset.side = (e.clientX < window.innerWidth / 2) ? SIDE_L : SIDE_R;

            if (canDismiss) {
                const out = el.dataset.side !== startSide ? 0
                    : (startSide === SIDE_L ? startX - e.clientX : e.clientX - startX);
                setPush(Math.max(0, out), e.clientX);
            }
        };

        const onUp = (e) => {
            if (pointerId === null || e.pointerId !== pointerId) return;
            pointerId = null;
            el.releasePointerCapture?.(e.pointerId);
            if (!dragging) return;
            dragging = false;

            if (canDismiss) {
                // 끝까지 밀어 넣고 놓았으면 치운다. pointercancel 은 보지 않는다 — clientX 가 0 으로
                // 오는 웹뷰가 있어 왼쪽 탭이 저절로 사라진다.
                if (e.type === 'pointerup' && atEdge(e.clientX)) {
                    el.classList.remove('is-dragging');
                    dismiss();
                    return;
                }
                setPush(0, e.clientX);
            }

            // clampTop 이 이미 보이는 구간으로 잘라주지만, 저장은 그 결과값으로만 한다 —
            // 화면 밖 좌표가 저장되면 다음에 열었을 때 탭이 없는 것처럼 보인다.
            // ⚠ 자리를 먼저 읽고 is-dragging 을 뗀다 — 떼는 순간 top 전환이 되살아나
            //   getBoundingClientRect() 가 전환 중간값을 돌려줄 수 있다.
            const { h } = range();
            const top = clampTop(curTop());     // 화면좌표로 통일 (style.top 은 배율이 섞인 값이다)
            el.classList.remove('is-dragging');
            setTop(top);

            const y = (top + h / 2) / window.innerHeight;
            if (isFinite(y) && y > 0 && y < 1) save(el.dataset.side, y);
            else                              clearPos();
        };

        const onClickCapture = (e) => {
            if (moved) {                        // 옮기자마자 눌린 것으로 처리되면 안 된다
                moved = false;
                e.preventDefault();
                e.stopImmediatePropagation();
                return;
            }
            if (opts.onTap) opts.onTap(e);
        };
        const onContextMenu = (e) => e.preventDefault();   // 길게 누를 때 선택/컨텍스트 메뉴 방지

        // 회전·주소창·키보드로 보이는 구간이 바뀌면 다시 끌어당긴다(저장값은 그대로 둔다).
        const onResize = () => applyPos();
        // 뒤로가기 복귀(bfcache)는 attach 를 다시 타지 않는다 — 여기서 한 번 더 검사한다.
        // 치웠던 탭도 되살린다 — 치우기는 '지금 보고 있는 화면'에만 유효하다.
        const onShow = (e) => {
            if (e.persisted) undismiss();
            applyPos({ validate: true });
        };
        const vv = window.visualViewport;

        el.addEventListener('click', onClickCapture, true);
        el.addEventListener('pointerdown', onDown);
        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerup', onUp);
        el.addEventListener('pointercancel', onUp);
        el.addEventListener('contextmenu', onContextMenu);
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        window.addEventListener('pageshow', onShow);
        // 키보드 개폐는 window resize 를 안 던지는 웹뷰가 있다. scroll 은 안 듣는다 —
        // 핀치줌 패닝마다 탭이 따라 움직이면 끌리는 것처럼 보인다.
        vv?.addEventListener('resize', onResize);

        applyPos({ validate: true });
        peek();

        return {
            peek,
            reset,
            side,
            destroy() {
                clearTimeout(peekTimer);
                clearTimeout(leaveTimer);
                el.removeEventListener('click', onClickCapture, true);
                el.removeEventListener('pointerdown', onDown);
                el.removeEventListener('pointermove', onMove);
                el.removeEventListener('pointerup', onUp);
                el.removeEventListener('pointercancel', onUp);
                el.removeEventListener('contextmenu', onContextMenu);
                window.removeEventListener('resize', onResize);
                window.removeEventListener('orientationchange', onResize);
                window.removeEventListener('pageshow', onShow);
                vv?.removeEventListener('resize', onResize);
                delete el.dataset.edgeTab;
            },
        };
    }
}
